import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { z } from 'zod';
import { BsFillExclamationTriangleFill, BsInfoCircle } from 'react-icons/bs';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Define Zod schemas for validation
const roomNumberSchema = z.string().min(1, { message: 'Room number is required' }).regex(/^\d+$/, {
  message: 'Must contain only digits',
});

const lastNameSchema = z
  .string()
  .min(1, { message: 'Last name is required' })
  .regex(/^[a-zA-Z\s'-]*$/, {
    message: 'Only letters, spaces, hyphens, and apostrophes allowed',
  });

const RoomnoCheckout = () => {
  const [roomNumber, setRoomNumber] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [errors, setErrors] = useState({ roomNumber: '', lastName: '' });
  const [touched, setTouched] = useState({
    roomNumber: false,
    lastName: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerData, setDisclaimerData] = useState(null);
  const [hotelContact, setHotelContact] = useState({
    email: '',
    phone: '',
    propertyname: '',
  });
  const [apiResponseData, setApiResponseData] = useState(null); // Store API response for later use

  const navigate = useNavigate();

  // Function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  const decryptUserData = async (encryptedData) => {
    try {
      if (encryptedData.email && encryptedData.email.length < 50) {
        return encryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: {
            email: encryptedData.email,
            phone_number: encryptedData.phone_number,
            country_code: encryptedData.country_code,
          },
        }),
      });

      if (response.status === 401) {
        return encryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  const getGuestData = async () => {
    // First, check localStorage for user data
    const userDataCache = localStorage.getItem('userDataCache');
    if (userDataCache) {
      try {
        const parsed = JSON.parse(userDataCache);
        if (parsed.data) {
          console.log('Found user data in localStorage:', parsed.data);
          setPhone(parsed.data.phone_number || '');
          setCountryCode(parsed.data.country_code || '');
          setEmail(parsed.data.email || '');

          return {
            firstname: parsed.data.firstname || '',
            lastname: parsed.data.lastname || '',
            email: parsed.data.email || '',
            phone: parsed.data.phone_number || '',
            country_code: parsed.data.country_code || '',
          };
        }
      } catch (e) {
        console.error('Error parsing user data cache:', e);
      }
    }

    // If not in localStorage, try to get from API
    const accessToken = Cookies.get('access_token');
    if (!accessToken) return null;

    try {
      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) return null;

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

      if (isDecrypted) {
        setPhone(profileData.phonenumber || '');
        setCountryCode(profileData.countrycode || '');
        setEmail(profileData.emailid || '');

        return {
          firstname: profileData.firstname || '',
          lastname: profileData.lastname || '',
          email: profileData.emailid || '',
          phone: profileData.phonenumber || '',
          country_code: profileData.countrycode || '',
        };
      }

      const decryptedFields = await decryptUserData({
        email: profileData.emailid,
        phone_number: profileData.phonenumber,
        country_code: profileData.countrycode,
      });

      setPhone(decryptedFields.phone_number || profileData.phonenumber || '');
      setCountryCode(decryptedFields.country_code || profileData.countrycode || '');
      setEmail(decryptedFields.email || profileData.emailid || '');

      return {
        firstname: profileData.firstname || '',
        lastname: profileData.lastname || '',
        email: decryptedFields.email || profileData.emailid || '',
        phone: decryptedFields.phone_number || profileData.phonenumber || '',
        country_code: decryptedFields.country_code || profileData.countrycode || '',
      };
    } catch (error) {
      console.error('Error fetching guest data:', error);
      return null;
    }
  };

  const validateField = (fieldName, value) => {
    let error = '';

    if (fieldName === 'roomNumber') {
      const result = roomNumberSchema.safeParse(value);
      if (!result.success) {
        error = result.error.errors[0].message;
      }
    } else if (fieldName === 'lastName') {
      const result = lastNameSchema.safeParse(value);
      if (!result.success) {
        error = result.error.errors[0].message;
      }
    }

    setErrors((prev) => ({ ...prev, [fieldName]: error }));
    return !error;
  };

  const validateFields = () => {
    const validRoom = validateField('roomNumber', roomNumber);
    const validLastName = validateField('lastName', lastName);
    return validRoom && validLastName;
  };

  const calculateCheckoutStatus = (checkoutDate, isEarlyCheckout, isLateCheckout) => {
    // Ensure we have a valid date
    if (!checkoutDate) {
      return {
        type: 'unknown',
        status: 'error',
        message: 'Checkout date not available',
        chargeApplied: false,
      };
    }

    const now = new Date();
    let checkoutDateTime;

    // Handle different date formats from API
    if (typeof checkoutDate === 'string') {
      checkoutDateTime = new Date(checkoutDate);
    } else if (checkoutDate instanceof Date) {
      checkoutDateTime = new Date(checkoutDate);
    } else {
      // If it's an object with date properties
      checkoutDateTime = new Date(checkoutDate.date || checkoutDate);
    }

    // Validate the date
    if (isNaN(checkoutDateTime.getTime())) {
      return {
        type: 'unknown',
        status: 'error',
        message: 'Invalid checkout date format',
        chargeApplied: false,
      };
    }

    // Set checkout time to 11 AM (hotel standard checkout time)
    checkoutDateTime.setHours(11, 0, 0, 0);

    // Check if it's early checkout (before scheduled checkout day)
    const isBeforeScheduledDay = now < new Date(checkoutDateTime.setHours(0, 0, 0, 0));

    if (isEarlyCheckout || isBeforeScheduledDay) {
      // Early checkout (before scheduled checkout day)
      if (now < checkoutDateTime) {
        return {
          type: 'early',
          status: 'no_charge',
          message: 'Early checkout before 11 AM - No additional charge',
          chargeApplied: false,
        };
      } else {
        return {
          type: 'early',
          status: 'extra_day_charge',
          message: 'Early checkout after 11 AM - Charged for an extra day',
          chargeApplied: true,
        };
      }
    }

    // Check if it's late checkout (on scheduled checkout day)
    if (isLateCheckout || !isBeforeScheduledDay) {
      const gracePeriodEnd = new Date(checkoutDateTime);
      gracePeriodEnd.setHours(15, 0, 0, 0); // 3 PM grace period end

      if (now <= gracePeriodEnd) {
        return {
          type: 'late',
          status: 'grace_period',
          message: 'Late checkout within grace period (until 3 PM) - No additional charge',
          chargeApplied: false,
        };
      } else {
        return {
          type: 'late',
          status: 'extra_day_charge',
          message: 'Late checkout after 3 PM - Charged for an extra day',
          chargeApplied: true,
        };
      }
    }

    // Default case (shouldn't normally reach here)
    return {
      type: 'normal',
      status: 'on_time',
      message: 'Checkout on time - No additional charge',
      chargeApplied: false,
    };
  };

  useEffect(() => {
    const fetchGuestEmail = async () => {
      const guestData = await getGuestData();
      if (guestData && guestData.email) {
        setEmail(guestData.email);
      }
      if (guestData && guestData.phone) {
        setPhone(guestData.phone);
      }
      if (guestData && guestData.country_code) {
        setCountryCode(guestData.country_code);
      }
    };

    const hotelPropertyCache = localStorage.getItem('hotelPropertyCache');
    if (hotelPropertyCache) {
      try {
        const parsed = JSON.parse(hotelPropertyCache);
        if (parsed.data) {
          setHotelContact({
            email: parsed.data.email || '',
            phone: parsed.data.phone || '',
            propertyname: parsed.data.propertyname || '',
          });
        }
      } catch (e) {
        console.error('Error parsing hotel property cache:', e);
      }
    }

    fetchGuestEmail();
  }, []);

  const handleRoomNumberChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setRoomNumber(value);
      validateField('roomNumber', value);
    } else {
      setErrors((prev) => ({
        ...prev,
        roomNumber: 'Must contain only digits',
      }));
    }
    setTouched((prev) => ({ ...prev, roomNumber: true }));
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z\s'-]*$/.test(value)) {
      setLastName(value);
      validateField('lastName', value);
    } else {
      setErrors((prev) => ({
        ...prev,
        lastName: 'Only letters, spaces, hyphens, and apostrophes allowed',
      }));
    }
    setTouched((prev) => ({ ...prev, lastName: true }));
  };

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    if (fieldName === 'roomNumber') {
      validateField('roomNumber', roomNumber);
    } else if (fieldName === 'lastName') {
      validateField('lastName', lastName);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    setTouched({ roomNumber: true, lastName: true });

    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      const accessToken = Cookies.get('access_token');
      const headers = {
        'Content-Type': 'application/json',
      };

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      // Get user data
      const guestData = await getGuestData();

      // Determine which identifier to use (email or phone)
      let identifier = '';
      let countrycode = '';

      if (guestData && guestData.email) {
        identifier = guestData.email;
        countrycode = ''; // No country code needed for email
        console.log('Using email as identifier:', identifier);
      } else if (guestData && guestData.phone) {
        identifier = guestData.phone;
        countrycode = guestData.country_code || '';
        console.log('Using phone as identifier:', identifier, 'with country code:', countrycode);
      } else {
        // Fallback: use last name only if no identifier available
        console.log('No identifier found, using only lastname');
        identifier = '';
        countrycode = '';
      }

      // Get today's date for desired_checkout_date parameter
      const desiredCheckoutDate = getTodayDate();

      // Build the API URL with identifier and countrycode
      let apiUrl = `${BQ_BASE_URL}/bq/api/checkout/${roomNumber}?lastname=${encodeURIComponent(
        lastName
      )}`;

      // Add identifier if available
      if (identifier) {
        apiUrl += `&identifier=${encodeURIComponent(identifier)}`;
      }

      // Add countrycode if available (only for phone numbers)
      if (countrycode) {
        apiUrl += `&countrycode=${encodeURIComponent(countrycode)}`;
      }

      // Add email if available (for backward compatibility)
      if (guestData && guestData.email) {
        apiUrl += `&email=${encodeURIComponent(guestData.email)}`;
      }

      // Add desired_checkout_date
      apiUrl += `&desired_checkout_date=${desiredCheckoutDate}`;

      console.log('API URL:', apiUrl); // For debugging

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });

      const responseData = await response.json();
      console.log('API Response:', responseData); // Debugging

      if (!response.ok) {
        const errorMessage = responseData.detail?.message || 'Failed to verify checkout details';
        const errorDetails =
          responseData.detail?.details || 'Please check your details and try again';
        throw new Error(`${errorMessage}\n\n${errorDetails}`);
      }

      if (responseData.success) {
        // Calculate checkout status based on the API response
        const checkoutStatus = calculateCheckoutStatus(
          responseData.reservation_details.checkout_date,
          responseData.early_checkout,
          responseData.late_checkout
        );

        console.log('Calculated checkout status:', checkoutStatus); // Debug the result

        // Store all necessary data
        localStorage.setItem('bookingId', responseData.bookingid);
        localStorage.setItem(
          'checkoutData',
          JSON.stringify({
            success: responseData.success,
            message: responseData.message,
            early_checkout: responseData.early_checkout,
            late_checkout: responseData.late_checkout,
            orderid: responseData.orderid,
            bookingid: responseData.bookingid,
            reservation_details: responseData.reservation_details,
            checkout_status: checkoutStatus,
            charge_details: responseData.charge_details,
          })
        );
        localStorage.setItem('earlyCheckout', responseData.early_checkout ? 'true' : 'false');
        localStorage.setItem('orderId', responseData.orderid);
        localStorage.setItem('primaryGuest', JSON.stringify(responseData.primary_guest));
        localStorage.setItem(
          'reservationDetails',
          JSON.stringify(responseData.reservation_details)
        );

        // Store the API response data for later use
        setApiResponseData(responseData);

        // Check if we need to show disclaimer or navigate directly
        if (checkoutStatus.type === 'late' && checkoutStatus.status === 'grace_period') {
          // For late checkout within grace period, navigate directly without showing disclaimer
          await handleCheckoutConfirmation(responseData);
        } else {
          // Show disclaimer popup for other checkout scenarios
          setDisclaimerData({
            checkoutStatus,
            chargeDetails: responseData.charge_details,
            reservationDetails: responseData.reservation_details,
          });
          setShowDisclaimer(true);
        }
      } else {
        const errorMsg = responseData.message || 'Verification failed';
        const errorDetails = responseData.details ? `\n\n${responseData.details}` : '';
        setPopupMessage(`${errorMsg}${errorDetails}`);
        setShowPopup(true);
      }
    } catch (error) {
      console.error('API Error:', error);
      setPopupMessage(
        error.message || 'An error occurred while verifying your details. Please try again.'
      );
      setShowPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckoutConfirmation = async (responseData) => {
    setIsLoading(true);
    try {
      const accessToken = Cookies.get('access_token');
      const headers = {
        'Content-Type': 'application/json',
        accept: 'application/json',
      };

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      // Get user data
      const guestData = await getGuestData();

      // Determine which identifier to use (email or phone)
      let identifier = '';
      let countrycode = '';

      if (guestData && guestData.email) {
        identifier = guestData.email;
        countrycode = ''; // No country code needed for email
      } else if (guestData && guestData.phone) {
        identifier = guestData.phone;
        countrycode = guestData.country_code || '';
      } else {
        identifier = '';
        countrycode = '';
      }

      // Get today's date for desired_checkout_date parameter
      const desiredCheckoutDate = getTodayDate();

      // Build the POST API URL with all parameters
      let apiUrl = `${BQ_BASE_URL}/bq/api/checkout/${roomNumber}?lastname=${encodeURIComponent(
        lastName
      )}`;

      // Add identifier if available
      if (identifier) {
        apiUrl += `&identifier=${encodeURIComponent(identifier)}`;
      }

      // Add countrycode if available (only for phone numbers)
      if (countrycode) {
        apiUrl += `&countrycode=${encodeURIComponent(countrycode)}`;
      }

      // Add email if available (for backward compatibility)
      if (guestData && guestData.email) {
        apiUrl += `&email=${encodeURIComponent(guestData.email)}`;
      }

      // Add desired_checkout_date
      apiUrl += `&desired_checkout_date=${desiredCheckoutDate}`;

      console.log('POST API URL:', apiUrl); // For debugging

      // Make the POST request to confirm checkout
      const postResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({}),
      });

      const postResponseData = await postResponse.json();
      console.log('POST API Response:', postResponseData);

      if (!postResponse.ok) {
        const errorMessage = postResponseData.detail?.message || 'Failed to confirm checkout';
        const errorDetails =
          postResponseData.detail?.details || 'Please check your details and try again';
        throw new Error(`${errorMessage}\n\n${errorDetails}`);
      }

      // If successful, navigate to the next page
      navigate('/booking-checkin');
    } catch (error) {
      console.error('Checkout confirmation error:', error);
      setPopupMessage(
        error.message || 'An error occurred while confirming your checkout. Please try again.'
      );
      setShowPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisclaimerContinue = async () => {
    setShowDisclaimer(false);

    // If we have API response data, use it to confirm checkout
    if (apiResponseData) {
      await handleCheckoutConfirmation(apiResponseData);
    } else {
      // Fallback: navigate directly if no API data is available
      navigate('/booking-checkin');
    }
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex justify-center items-center min-h-1 ">
      <div className="w-full max-w-md ">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-md font-medium text-black mb-2" htmlFor="roomNumber">
              Room Number
            </label>
            <input
              id="roomNumber"
              type="text"
              value={roomNumber}
              onChange={handleRoomNumberChange}
              onBlur={() => handleBlur('roomNumber')}
              className={`w-full p-2 border rounded ${
                errors.roomNumber && touched.roomNumber ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your room number"
              inputMode="numeric"
            />
            <div className="h-2">
              {errors.roomNumber && touched.roomNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.roomNumber}</p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-md font-medium text-black mb-2" htmlFor="lastName">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={lastName}
              onChange={handleLastNameChange}
              onBlur={() => handleBlur('lastName')}
              className={`w-full p-2 border rounded ${
                errors.lastName && touched.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your last name"
            />
            <div className="h-2">
              {errors.lastName && touched.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {apiError && <div className="mb-4 text-red-500 text-sm">{apiError}</div>}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full mt-1 bg-black text-white py-2 px-4 rounded hover:text-gray-300 transition ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? 'Verifying...' : 'Search Booking'}
          </button>
        </form>
      </div>

      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="relative bg-white rounded-lg shadow-lg max-w-[550px] w-full p-6 mx-4">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="text-center pt-2">
              <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-2" />
              <h2 className="text-gray-900 text-lg font-semibold mb-6">Unable to Proceed</h2>
              <p className="text-red-600 text-md font-medium mb-6">
                {popupMessage.split('\n\n').map((part, index) => (
                  <span key={index}>
                    {part}
                    {index < popupMessage.split('\n\n').length - 1 && ' '}
                  </span>
                ))}
              </p>
              <p className="text-gray-700 text-sm mb-4 mt-2">
                In case you need an assistance please contact our reception support team at{' '}
                <a href={`mailto:${hotelContact.email}`} className="text-blue-600 hover:underline">
                  {hotelContact.email}
                </a>{' '}
                or call us at{' '}
                <a href={`tel:${hotelContact.phone}`} className="text-blue-600 hover:underline">
                  {hotelContact.phone}
                </a>
              </p>

              <button
                onClick={() => setShowPopup(false)}
                className="px-5 py-2 bg-black text-white rounded-md font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {showDisclaimer && disclaimerData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-7">
            <h2 className="text-xl font-bold mb-4">
              {disclaimerData.checkoutStatus.type === 'early'
                ? 'Early Checkout Notice'
                : 'Late Checkout Notice'}
            </h2>

            {disclaimerData.checkoutStatus.type === 'early' &&
              !disclaimerData.checkoutStatus.chargeApplied && (
                <p className="mb-4">
                  You are trying to check out earlier than your scheduled check-out date of{' '}
                  <strong>{formatDate(disclaimerData.reservationDetails.checkout_date)}</strong>.
                  Are you sure you want to continue?
                </p>
              )}

            {disclaimerData.checkoutStatus.type === 'early' &&
              disclaimerData.checkoutStatus.chargeApplied && (
                <p className="mb-4">
                  You are checking out early instead of your scheduled check-out on{' '}
                  <strong>{formatDate(disclaimerData.reservationDetails.checkout_date)}</strong>.
                  Since you are leaving after 11 AM, it will be considered as an additional day, and
                  you will incur a charge of one extra day .
                </p>
              )}

            {disclaimerData.checkoutStatus.type === 'late' &&
              disclaimerData.checkoutStatus.chargeApplied && (
                <p className="mb-4">
                  Since your check-out was after <strong>3:00 PM</strong>, an additional charge
                  equivalent to one extra day has been applied .
                </p>
              )}

            <div className="flex justify-end space-x-4 mt-10">
              <button
                onClick={handleDisclaimerCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleDisclaimerContinue}
                disabled={isLoading}
                className={`px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Processing...' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomnoCheckout;

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import Cookies from "js-cookie";
// import { z } from "zod";
// import { BsFillExclamationTriangleFill, BsInfoCircle } from "react-icons/bs";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Define Zod schemas for validation
// const roomNumberSchema = z
//   .string()
//   .min(1, { message: "Room number is required" })
//   .regex(/^\d+$/, {
//     message: "Must contain only digits",
//   });

// const lastNameSchema = z
//   .string()
//   .min(1, { message: "Last name is required" })
//   .regex(/^[a-zA-Z\s'-]*$/, {
//     message: "Only letters, spaces, hyphens, and apostrophes allowed",
//   });

// const RoomnoCheckout = () => {
//   const [roomNumber, setRoomNumber] = useState("");
//   const [lastName, setLastName] = useState("");
//   const [email, setEmail] = useState("");
//   const [errors, setErrors] = useState({ roomNumber: "", lastName: "" });
//   const [touched, setTouched] = useState({
//     roomNumber: false,
//     lastName: false,
//   });
//   const [isLoading, setIsLoading] = useState(false);
//   const [apiError, setApiError] = useState("");
//   const [showPopup, setShowPopup] = useState(false);
//   const [popupMessage, setPopupMessage] = useState("");
//   const [showDisclaimer, setShowDisclaimer] = useState(false);
//   const [disclaimerData, setDisclaimerData] = useState(null);
//   const [hotelContact, setHotelContact] = useState({
//     email: "",
//     phone: "",
//     propertyname: "",
//   });
//   const [apiResponseData, setApiResponseData] = useState(null); // Store API response for later use

//   const navigate = useNavigate();

//   // Function to get today's date in YYYY-MM-DD format
//   const getTodayDate = () => {
//     const today = new Date();
//     const year = today.getFullYear();
//     const month = String(today.getMonth() + 1).padStart(2, "0");
//     const day = String(today.getDate()).padStart(2, "0");
//     return `${year}-${month}-${day}`;
//   };

//   const getGuestIdFromToken = (token) => {
//     try {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       return payload.sub;
//     } catch (e) {
//       console.error("Error parsing token:", e);
//       return null;
//     }
//   };

//   const decryptUserData = async (encryptedData) => {
//     try {
//       if (encryptedData.email && encryptedData.email.length < 50) {
//         return encryptedData;
//       }

//       const accessToken = Cookies.get("access_token");
//       if (!accessToken) {
//         throw new Error("No access token found");
//       }

//       const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//         body: JSON.stringify({
//           encrypted_fields: {
//             email: encryptedData.email,
//             phone_number: encryptedData.phone_number,
//             country_code: encryptedData.country_code,
//           },
//         }),
//       });

//       if (response.status === 401) {
//         return encryptedData;
//       }

//       if (!response.ok) {
//         throw new Error(`Decryption failed: ${response.statusText}`);
//       }

//       return await response.json();
//     } catch (error) {
//       console.error("Decryption error:", error);
//       return encryptedData;
//     }
//   };

//   const getGuestData = async () => {
//     const accessToken = Cookies.get("access_token");
//     if (!accessToken) return null;

//     try {
//       const guestId =
//         Cookies.get("guest_id") || getGuestIdFromToken(accessToken);
//       if (!guestId) return null;

//       const profileResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (!profileResponse.ok) {
//         throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//       }

//       const profileData = await profileResponse.json();
//       const isDecrypted =
//         profileData.emailid && profileData.emailid.length < 50;

//       if (isDecrypted) {
//         return {
//           firstname: profileData.firstname || "",
//           lastname: profileData.lastname || "",
//           email: profileData.emailid || "",
//         };
//       }

//       const decryptedFields = await decryptUserData({
//         email: profileData.emailid,
//         phone_number: profileData.phonenumber,
//         country_code: profileData.countrycode,
//       });

//       return {
//         firstname: profileData.firstname || "",
//         lastname: profileData.lastname || "",
//         email: decryptedFields.email || profileData.emailid || "",
//       };
//     } catch (error) {
//       console.error("Error fetching guest data:", error);
//       return null;
//     }
//   };

//   const validateField = (fieldName, value) => {
//     let error = "";

//     if (fieldName === "roomNumber") {
//       const result = roomNumberSchema.safeParse(value);
//       if (!result.success) {
//         error = result.error.errors[0].message;
//       }
//     } else if (fieldName === "lastName") {
//       const result = lastNameSchema.safeParse(value);
//       if (!result.success) {
//         error = result.error.errors[0].message;
//       }
//     }

//     setErrors((prev) => ({ ...prev, [fieldName]: error }));
//     return !error;
//   };

//   const validateFields = () => {
//     const validRoom = validateField("roomNumber", roomNumber);
//     const validLastName = validateField("lastName", lastName);
//     return validRoom && validLastName;
//   };

//   const calculateCheckoutStatus = (
//     checkoutDate,
//     isEarlyCheckout,
//     isLateCheckout
//   ) => {
//     // Ensure we have a valid date
//     if (!checkoutDate) {
//       return {
//         type: "unknown",
//         status: "error",
//         message: "Checkout date not available",
//         chargeApplied: false,
//       };
//     }

//     const now = new Date();
//     let checkoutDateTime;

//     // Handle different date formats from API
//     if (typeof checkoutDate === "string") {
//       checkoutDateTime = new Date(checkoutDate);
//     } else if (checkoutDate instanceof Date) {
//       checkoutDateTime = new Date(checkoutDate);
//     } else {
//       // If it's an object with date properties
//       checkoutDateTime = new Date(checkoutDate.date || checkoutDate);
//     }

//     // Validate the date
//     if (isNaN(checkoutDateTime.getTime())) {
//       return {
//         type: "unknown",
//         status: "error",
//         message: "Invalid checkout date format",
//         chargeApplied: false,
//       };
//     }

//     // Set checkout time to 1 PM (hotel standard checkout time)
//     checkoutDateTime.setHours(11, 0, 0, 0);

//     // Check if it's early checkout (before scheduled checkout day)
//     const isBeforeScheduledDay =
//       now < new Date(checkoutDateTime.setHours(0, 0, 0, 0));

//     if (isEarlyCheckout || isBeforeScheduledDay) {
//       // Early checkout (before scheduled checkout day)
//       if (now < checkoutDateTime) {
//         return {
//           type: "early",
//           status: "no_charge",
//           message: "Early checkout before 1 PM - No additional charge",
//           chargeApplied: false,
//         };
//       } else {
//         return {
//           type: "early",
//           status: "extra_day_charge",
//           message: "Early checkout after 1 PM - Charged for an extra day",
//           chargeApplied: true,
//         };
//       }
//     }

//     // Check if it's late checkout (on scheduled checkout day)
//     if (isLateCheckout || !isBeforeScheduledDay) {
//       const gracePeriodEnd = new Date(checkoutDateTime);
//       gracePeriodEnd.setHours(15, 0, 0, 0); // 3 PM grace period end

//       if (now <= gracePeriodEnd) {
//         // return {
//         //   type: "late",
//         //   status: "grace_period",
//         //   message:
//         //     "Late checkout within grace period (until 3 PM) - No additional charge",
//         //   chargeApplied: false,
//         // };
//       } else {
//         return {
//           type: "late",
//           status: "extra_day_charge",
//           message: "Late checkout after 3 PM - Charged for an extra day",
//           chargeApplied: true,
//         };
//       }
//     }

//     // Default case (shouldn't normally reach here)
//     return {
//       type: "normal",
//       status: "on_time",
//       message: "Checkout on time - No additional charge",
//       chargeApplied: false,
//     };
//   };

//   useEffect(() => {
//     const fetchGuestEmail = async () => {
//       const guestData = await getGuestData();
//       if (guestData && guestData.email) {
//         setEmail(guestData.email);
//       }
//     };

//     const hotelPropertyCache = localStorage.getItem("hotelPropertyCache");
//     if (hotelPropertyCache) {
//       try {
//         const parsed = JSON.parse(hotelPropertyCache);
//         if (parsed.data) {
//           setHotelContact({
//             email: parsed.data.email || "",
//             phone: parsed.data.phone || "",
//             propertyname: parsed.data.propertyname || "",
//           });
//         }
//       } catch (e) {
//         console.error("Error parsing hotel property cache:", e);
//       }
//     }

//     fetchGuestEmail();
//   }, []);

//   const handleRoomNumberChange = (e) => {
//     const value = e.target.value;
//     if (/^\d*$/.test(value)) {
//       setRoomNumber(value);
//       validateField("roomNumber", value);
//     } else {
//       setErrors((prev) => ({
//         ...prev,
//         roomNumber: "Must contain only digits",
//       }));
//     }
//     setTouched((prev) => ({ ...prev, roomNumber: true }));
//   };

//   const handleLastNameChange = (e) => {
//     const value = e.target.value;
//     if (/^[a-zA-Z\s'-]*$/.test(value)) {
//       setLastName(value);
//       validateField("lastName", value);
//     } else {
//       setErrors((prev) => ({
//         ...prev,
//         lastName: "Only letters, spaces, hyphens, and apostrophes allowed",
//       }));
//     }
//     setTouched((prev) => ({ ...prev, lastName: true }));
//   };

//   const handleBlur = (fieldName) => {
//     setTouched((prev) => ({ ...prev, [fieldName]: true }));
//     if (fieldName === "roomNumber") {
//       validateField("roomNumber", roomNumber);
//     } else if (fieldName === "lastName") {
//       validateField("lastName", lastName);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setApiError("");

//     setTouched({ roomNumber: true, lastName: true });

//     if (!validateFields()) {
//       return;
//     }

//     setIsLoading(true);

//     try {
//       const accessToken = Cookies.get("access_token");
//       const headers = {
//         "Content-Type": "application/json",
//       };

//       if (accessToken) {
//         headers.Authorization = `Bearer ${accessToken}`;
//       }

//       // Get today's date for desired_checkout_date parameter
//       const desiredCheckoutDate = getTodayDate();

//       // Build the API URL with all parameters including desired_checkout_date
//       const apiUrl = `${BQ_BASE_URL}/bq/api/checkout/${roomNumber}?lastname=${encodeURIComponent(
//         lastName
//       )}${
//         email ? `&email=${encodeURIComponent(email)}` : ""
//       }&desired_checkout_date=${desiredCheckoutDate}`;

//       console.log("API URL:", apiUrl); // For debugging

//       const response = await fetch(apiUrl, {
//         method: "GET",
//         headers: headers,
//       });

//       const responseData = await response.json();
//       console.log("API Response:", responseData); // Debugging

//       if (!response.ok) {
//         const errorMessage =
//           responseData.detail?.message || "Failed to verify checkout details";
//         const errorDetails =
//           responseData.detail?.details ||
//           "Please check your details and try again";
//         throw new Error(`${errorMessage}\n\n${errorDetails}`);
//       }

//       if (responseData.success) {
//         // Calculate checkout status based on the API response
//         const checkoutStatus = calculateCheckoutStatus(
//           responseData.reservation_details.checkout_date,
//           responseData.early_checkout,
//           responseData.late_checkout
//         );

//         console.log("Calculated checkout status:", checkoutStatus); // Debug the result

//         // Store all necessary data
//         localStorage.setItem("bookingId", responseData.bookingid);
//         localStorage.setItem(
//           "checkoutData",
//           JSON.stringify({
//             success: responseData.success,
//             message: responseData.message,
//             early_checkout: responseData.early_checkout,
//             late_checkout: responseData.late_checkout,
//             orderid: responseData.orderid,
//             bookingid: responseData.bookingid,
//             reservation_details: responseData.reservation_details,
//             checkout_status: checkoutStatus,
//             charge_details: responseData.charge_details,
//           })
//         );
//         localStorage.setItem(
//           "earlyCheckout",
//           responseData.early_checkout ? "true" : "false"
//         );
//         localStorage.setItem("orderId", responseData.orderid);
//         localStorage.setItem(
//           "primaryGuest",
//           JSON.stringify(responseData.primary_guest)
//         );
//         localStorage.setItem(
//           "reservationDetails",
//           JSON.stringify(responseData.reservation_details)
//         );

//         // Store the API response data for later use
//         setApiResponseData(responseData);

//         // Check if we need to show disclaimer or navigate directly
//         if (
//           checkoutStatus.type === "late" &&
//           checkoutStatus.status === "grace_period"
//         ) {
//           // For late checkout within grace period, navigate directly without showing disclaimer
//           await handleCheckoutConfirmation(responseData);
//         } else {
//           // Show disclaimer popup for other checkout scenarios
//           setDisclaimerData({
//             checkoutStatus,
//             chargeDetails: responseData.charge_details,
//             reservationDetails: responseData.reservation_details,
//           });
//           setShowDisclaimer(true);
//         }
//       } else {
//         const errorMsg = responseData.message || "Verification failed";
//         const errorDetails = responseData.details
//           ? `\n\n${responseData.details}`
//           : "";
//         setPopupMessage(`${errorMsg}${errorDetails}`);
//         setShowPopup(true);
//       }
//     } catch (error) {
//       console.error("API Error:", error);
//       setPopupMessage(
//         error.message ||
//           "An error occurred while verifying your details. Please try again."
//       );
//       setShowPopup(true);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleCheckoutConfirmation = async (responseData) => {
//     setIsLoading(true);
//     try {
//       const accessToken = Cookies.get("access_token");
//       const headers = {
//         "Content-Type": "application/json",
//         accept: "application/json",
//       };

//       if (accessToken) {
//         headers.Authorization = `Bearer ${accessToken}`;
//       }

//       // Get today's date for desired_checkout_date parameter
//       const desiredCheckoutDate = getTodayDate();

//       // Build the POST API URL with all parameters including desired_checkout_date
//       const apiUrl = `${BQ_BASE_URL}/bq/api/checkout/${roomNumber}?lastname=${encodeURIComponent(
//         lastName
//       )}${
//         email ? `&email=${encodeURIComponent(email)}` : ""
//       }&desired_checkout_date=${desiredCheckoutDate}`;

//       console.log("POST API URL:", apiUrl); // For debugging

//       // Make the POST request to confirm checkout
//       const postResponse = await fetch(apiUrl, {
//         method: "POST",
//         headers: headers,
//         body: JSON.stringify({}),
//       });

//       const postResponseData = await postResponse.json();
//       console.log("POST API Response:", postResponseData);

//       if (!postResponse.ok) {
//         const errorMessage =
//           postResponseData.detail?.message || "Failed to confirm checkout";
//         const errorDetails =
//           postResponseData.detail?.details ||
//           "Please check your details and try again";
//         throw new Error(`${errorMessage}\n\n${errorDetails}`);
//       }

//       // If successful, navigate to the next page
//       navigate("/booking-checkin");
//     } catch (error) {
//       console.error("Checkout confirmation error:", error);
//       setPopupMessage(
//         error.message ||
//           "An error occurred while confirming your checkout. Please try again."
//       );
//       setShowPopup(true);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleDisclaimerContinue = async () => {
//     setShowDisclaimer(false);

//     // If we have API response data, use it to confirm checkout
//     if (apiResponseData) {
//       await handleCheckoutConfirmation(apiResponseData);
//     } else {
//       // Fallback: navigate directly if no API data is available
//       navigate("/booking-checkin");
//     }
//   };

//   const handleDisclaimerCancel = () => {
//     setShowDisclaimer(false);
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-US", {
//       weekday: "long",
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//     });
//   };

//   const formatTime = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleTimeString("en-US", {
//       hour: "2-digit",
//       minute: "2-digit",
//     });
//   };

//   return (
//     <div className="flex justify-center items-center min-h-1 ">
//       <div className="w-full max-w-md ">
//         <form onSubmit={handleSubmit}>
//           <div className="mb-4">
//             <label
//               className="block text-md font-medium text-black mb-2"
//               htmlFor="roomNumber"
//             >
//               Room Number
//             </label>
//             <input
//               id="roomNumber"
//               type="text"
//               value={roomNumber}
//               onChange={handleRoomNumberChange}
//               onBlur={() => handleBlur("roomNumber")}
//               className={`w-full p-2 border rounded ${
//                 errors.roomNumber && touched.roomNumber
//                   ? "border-red-500"
//                   : "border-gray-300"
//               }`}
//               placeholder="Enter your room number"
//               inputMode="numeric"
//             />
//             <div className="h-2">
//               {errors.roomNumber && touched.roomNumber && (
//                 <p className="text-red-500 text-sm mt-1">{errors.roomNumber}</p>
//               )}
//             </div>
//           </div>

//           <div className="mb-6">
//             <label
//               className="block text-md font-medium text-black mb-2"
//               htmlFor="lastName"
//             >
//               Last Name
//             </label>
//             <input
//               id="lastName"
//               type="text"
//               value={lastName}
//               onChange={handleLastNameChange}
//               onBlur={() => handleBlur("lastName")}
//               className={`w-full p-2 border rounded ${
//                 errors.lastName && touched.lastName
//                   ? "border-red-500"
//                   : "border-gray-300"
//               }`}
//               placeholder="Enter your last name"
//             />
//             <div className="h-2">
//               {errors.lastName && touched.lastName && (
//                 <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
//               )}
//             </div>
//           </div>

//           {apiError && (
//             <div className="mb-4 text-red-500 text-sm">{apiError}</div>
//           )}

//           <button
//             type="submit"
//             disabled={isLoading}
//             className={`w-full mt-1 bg-black text-white py-2 px-4 rounded hover:text-gray-300 transition ${
//               isLoading ? "opacity-50 cursor-not-allowed" : ""
//             }`}
//           >
//             {isLoading ? "Verifying..." : "Search Booking"}
//           </button>
//         </form>
//       </div>

//       {showPopup && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
//           <div className="relative bg-white rounded-lg shadow-lg max-w-[550px] w-full p-6 mx-4">
//             <button
//               onClick={() => setShowPopup(false)}
//               className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
//             >
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 className="h-6 w-6"
//                 fill="none"
//                 viewBox="0 0 24 24"
//                 stroke="currentColor"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M6 18L18 6M6 6l12 12"
//                 />
//               </svg>
//             </button>

//             <div className="text-center pt-2">
//               <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-2" />
//               <h2 className="text-gray-900 text-lg font-semibold mb-6">
//                 Unable to Proceed
//               </h2>
//               <p className="text-red-600 text-md font-medium mb-6">
//                 {popupMessage.split("\n\n").map((part, index) => (
//                   <span key={index}>
//                     {part}
//                     {index < popupMessage.split("\n\n").length - 1 && " "}
//                   </span>
//                 ))}
//               </p>
//               <p className="text-gray-700 text-sm mb-4 mt-2">
//                 In case you need an assistance please contact our reception
//                 support team at{" "}
//                 <a
//                   href={`mailto:${hotelContact.email}`}
//                   className="text-blue-600 hover:underline"
//                 >
//                   {hotelContact.email}
//                 </a>{" "}
//                 or call us at{" "}
//                 <a
//                   href={`tel:${hotelContact.phone}`}
//                   className="text-blue-600 hover:underline"
//                 >
//                   {hotelContact.phone}
//                 </a>
//               </p>

//               <button
//                 onClick={() => setShowPopup(false)}
//                 className="px-5 py-2 bg-black text-white rounded-md font-medium"
//               >
//                 Try Again
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {showDisclaimer && disclaimerData && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg max-w-lg w-full p-7">
//             <h2 className="text-xl font-bold mb-4">
//               {disclaimerData.checkoutStatus.type === "early"
//                 ? "Early Checkout Notice"
//                 : "Late Checkout Notice"}
//             </h2>

//             {disclaimerData.checkoutStatus.type === "early" &&
//               !disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   You are trying to check out earlier than your scheduled
//                   check-out date of{" "}
//                   <strong>
//                     {formatDate(
//                       disclaimerData.reservationDetails.checkout_date
//                     )}
//                   </strong>
//                   . Are you sure you want to continue?
//                 </p>
//               )}

//             {disclaimerData.checkoutStatus.type === "early" &&
//               disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   You are checking out early instead of your scheduled check-out
//                   on{" "}
//                   <strong>
//                     {formatDate(
//                       disclaimerData.reservationDetails.checkout_date
//                     )}
//                   </strong>
//                   . Since you are leaving after 1 PM, it will be considered as
//                   an additional day, and you will incur a charge of one extra
//                   day .
//                 </p>
//               )}

//             {/* {disclaimerData.checkoutStatus.type === "late" &&
//               !disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   You are checking out late but within the grace period (until 3
//                   PM). No additional charges will be applied.
//                 </p>
//               )} */}

//             {disclaimerData.checkoutStatus.type === "late" &&
//               disclaimerData.checkoutStatus.chargeApplied && (
//                 <p className="mb-4">
//                   Since your check-out was after <strong>3:00 PM</strong>, an
//                   additional charge equivalent to one extra day has been applied
//                   .
//                 </p>
//               )}

//             <div className="flex justify-end space-x-4 mt-10">
//               <button
//                 onClick={handleDisclaimerCancel}
//                 className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDisclaimerContinue}
//                 disabled={isLoading}
//                 className={`px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 ${
//                   isLoading ? "opacity-50 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isLoading ? "Processing..." : "Proceed"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default RoomnoCheckout;
